import React, { useState, useEffect, useRef, useCallback } from "react";
import { __ } from "services/Translator";
import api from "services/api";
import config from "configs/config";
import { filterByCountry } from "utils/registrationStates";
import {
  getRequestCountry,
  resolveGeoCountry,
  COUNTRY_CHANGED_EVENT,
} from "services/requestCountry";

const FEATURE_CARDS_API = "/cms/home-feature-cards/true";
const FEATURE_CARDS_PARAMS = { sort: "sortOrder,ASC", allFields: true };

const DEFAULT_FEATURES = [
  {
    image: require("assets/play365/images/features/feature-banner-one.webp"),
    smallTitle: "",
    title: __("WEEKLY"),
    subTitle: __("TOURNAMENTS"),
    description:
      "Climb the leaderboard and grab your share of gold! Massive prizes – race fast, play smart, win big!",
    colorTheme: "white",
  },
  {
    image: require("assets/play365/images/features/feature-banner-two.webp"),
    smallTitle: __("First Deposit"),
    title: __("Welcome"),
    subTitle: "Bonus",
    description:
      "Step into the Clover and claim your golden start! Get up to 2,500 bonus and begin winning big today.",
    colorTheme: "white",
  },
  {
    image: require("assets/play365/images/features/feature-banner-three.webp"),
    smallTitle: "",
    title: __("VIP"),
    subTitle: __("Perks"),
    description:
      "Every spin counts toward exclusive Golden Clover rewards! Unlock cashback, higher limits & your personal VIP host.",
    colorTheme: "white",
  },
  {
    image: require("assets/play365/images/features/feature-banner-four.webp"),
    smallTitle: __("More Than"),
    title: "",
    subTitle: "10,000+ CASINO GAMES",
    description:
      "Spin the reels of fortune with Irish luck on your side! Play top slots, jackpots & bonus buys from the best providers",
    colorTheme: "white",
  },
  {
    image: require("assets/play365/images/features/feature-banner-five.webp"),
    smallTitle: __("Play Our"),
    title: __("Crash"),
    subTitle: "Games",
    description:
      "Boost your winnings with explosive multipliers! Play smart– every second counts!",
    colorTheme: "dark-brown",
  },
  {
    image: require("assets/play365/images/features/feature-banner-six.webp"),
    smallTitle: "",
    title: __("Live "),
    subTitle: "Casino",
    description:
      "Step into real-time action with professional dealers! Experience the thrill of Roulette, Blackjack & Baccarat — anytime, anywhere.",
    colorTheme: "white",
  },
];

const VALID_COLOR_THEMES = new Set([
  "white",
  "gold",
  "cream",
  "yellow",
  "emerald",
  "dark-brown",
  "charcoal",
  "black",
]);

const resolveFeatureImage = (image) => {
  if (!image) return "";
  if (typeof image !== "string") return "";

  if (image.startsWith("http") || image.startsWith("//")) {
    return image;
  }

  if (image.startsWith("/")) {
    const base = config.fileSourceUrl || config.publicDir || "";
    return base ? `${base.replace(/\/$/, "")}${image}` : image;
  }

  return image;
};

const isRecordEnabled = (record) => {
  const value = record?.isEnabled;

  if (value === undefined || value === null) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length > 0 && value.some((item) => item === "1" || item === 1);
  }

  return value && value !== "0" && value !== "false" && value !== "[]";
};

const normalizeCmsFeature = (record) => {
  const colorTheme = VALID_COLOR_THEMES.has(record?.colorTheme)
    ? record.colorTheme
    : "white";

  return {
    id: record?.id,
    image: resolveFeatureImage(record?.backgroundImage),
    smallTitle: record?.smallTitle || "",
    title: record?.title || "",
    subTitle: record?.subTitle || "",
    description: record?.description || "",
    colorTheme,
  };
};

const getVisibleCount = () => {
  const width = window.innerWidth;
  if (width < 768) return 1;
  if (width >= 768 && width <= 991) return 2;
  return 3;
};

const buildVisibleIndexes = (slotCount, featureCount) =>
  Array.from({ length: Math.min(slotCount, featureCount) }, (_, i) => i);

export default function FeatureItems() {
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [cmsLoaded, setCmsLoaded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(getVisibleCount());
  const [visibleIndexes, setVisibleIndexes] = useState(() =>
    buildVisibleIndexes(getVisibleCount(), DEFAULT_FEATURES.length)
  );
  const [crossfadeSlots, setCrossfadeSlots] = useState({});
  const isCrossfadingRef = useRef(false);
  const rotationStepRef = useRef(0);
  const visibleIndexesRef = useRef(visibleIndexes);
  const visibleCountRef = useRef(visibleCount);
  const featuresRef = useRef(features);
  const requestSeqRef = useRef(0);

  const CROSSFADE_MS = 600;
  const ROTATION_INTERVAL_MS = 3000;

  const fetchFeatureCards = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    try {
      // Resolve the effective country (manual selector -> account -> geo) so the
      // manual override is honoured, consistent with the rest of the site.
      await resolveGeoCountry(api);
      const visitorCountry = getRequestCountry();
      const response = await api.get(FEATURE_CARDS_API, FEATURE_CARDS_PARAMS);
      if (seq !== requestSeqRef.current) return; // superseded by a newer switch
      if (response.status !== 200) return;

      const data = response?.data?.data ?? response?.data ?? [];
      const cmsFeatures = filterByCountry(Array.isArray(data) ? data : [], visitorCountry)
        .filter(isRecordEnabled)
        .map(normalizeCmsFeature)
        .filter((feature) => feature.image && (feature.title || feature.subTitle));

      setFeatures(cmsFeatures.length > 0 ? cmsFeatures : DEFAULT_FEATURES);
    } catch (error) {
      console.error("Failed to fetch home feature cards:", error);
    } finally {
      if (seq === requestSeqRef.current) setCmsLoaded(true);
    }
  }, []);

  useEffect(() => {
    visibleIndexesRef.current = visibleIndexes;
  }, [visibleIndexes]);

  useEffect(() => {
    visibleCountRef.current = visibleCount;
  }, [visibleCount]);

  useEffect(() => {
    featuresRef.current = features;
  }, [features]);

  useEffect(() => {
    fetchFeatureCards();
    // Refetch in place when the country changes — no page reload.
    const onChange = () => fetchFeatureCards();
    window.addEventListener(COUNTRY_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(COUNTRY_CHANGED_EVENT, onChange);
  }, [fetchFeatureCards]);

  useEffect(() => {
    const slotCount = getVisibleCount();
    setVisibleCount(slotCount);
    setVisibleIndexes(buildVisibleIndexes(slotCount, features.length));
    rotationStepRef.current = 0;
  }, [features]);

  useEffect(() => {
    const handleResize = () => {
      const newCount = getVisibleCount();
      const featureCount = featuresRef.current.length;
      if (newCount !== visibleCountRef.current) {
        setVisibleCount(newCount);
        setVisibleIndexes(buildVisibleIndexes(newCount, featureCount));
        rotationStepRef.current = 0;
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isCrossfadingRef.current) return;

      const count = visibleCountRef.current;
      const indexes = visibleIndexesRef.current;
      const featureList = featuresRef.current;
      if (count >= featureList.length) return;

      const hiddenIndexes = featureList
        .map((_, i) => i)
        .filter((i) => !indexes.includes(i));
      if (hiddenIndexes.length === 0) return;

      const step = rotationStepRef.current;
      const slotIdx = step % count;
      const incoming = (count + step) % featureList.length;
      const outgoing = indexes[slotIdx];

      rotationStepRef.current = step + 1;
      isCrossfadingRef.current = true;
      setCrossfadeSlots({
        [slotIdx]: { outgoing, incoming },
      });

      setTimeout(() => {
        setVisibleIndexes((prev) =>
          prev.map((idx, i) => (i === slotIdx ? incoming : idx))
        );
        setCrossfadeSlots({});
        isCrossfadingRef.current = false;
      }, CROSSFADE_MS);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  const getColorThemeClass = (colorTheme) =>
    VALID_COLOR_THEMES.has(colorTheme)
      ? `feature-card--text-${colorTheme}`
      : "feature-card--text-white";

  const renderFeatureCard = (feature, animationClass = "") => (
    <div
      className={`feature-card ${getColorThemeClass(feature.colorTheme)} ${animationClass}`.trim()}
    >
      <div className="icon">
        <img alt="" src={feature.image} loading="lazy" />
      </div>
      <div className="features-content">
        {feature.smallTitle ? <h6>{feature.smallTitle}</h6> : null}
        {feature.title ? <h2>{feature.title}</h2> : null}
        {feature.subTitle ? <h3>{feature.subTitle}</h3> : null}
        {feature.description ? (
          <p className="feature-description">{feature.description}</p>
        ) : null}
      </div>
    </div>
  );

  if (!cmsLoaded && features.length === 0) {
    return null;
  }

  if (features.length === 0) {
    return null;
  }

  const displayIndexes =
    visibleCount >= features.length
      ? features.map((_, index) => index)
      : visibleIndexes;

  return (
    <div className="features-container">
      {displayIndexes.map((featureIndex, slotIdx) => {
        const crossfade = crossfadeSlots[slotIdx];

        if (crossfade) {
          return (
            <div key={slotIdx} className="feature-card-slot">
              {renderFeatureCard(
                features[crossfade.outgoing],
                "feature-card-crossfade-out"
              )}
              {renderFeatureCard(
                features[crossfade.incoming],
                "feature-card-crossfade-in"
              )}
            </div>
          );
        }

        return (
          <React.Fragment key={features[featureIndex]?.id ?? featureIndex}>
            {renderFeatureCard(features[featureIndex])}
          </React.Fragment>
        );
      })}
    </div>
  );
}
